/**
 * Sentry integration for the Mondivago web app.
 *
 * Initializes Sentry Cloud error tracking. If `VITE_SENTRY_DSN` is not set
 * (e.g. local development), Sentry is NOT initialized — all calls become no-ops.
 *
 * Usage:
 *   import { sentry, initSentry } from '@/shared/lib/sentry';
 *   initSentry();                       // call once at app bootstrap
 *   sentry.captureException(err);       // report an error
 */

import * as Sentry from '@sentry/react';

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const isProd = import.meta.env.PROD;

let initialized = false;

export function initSentry(): void {
  if (!DSN || initialized) {
    return;
  }

  Sentry.init({
    dsn: DSN,
    environment: isProd ? 'production' : 'development',
    release: import.meta.env.VITE_SENTRY_RELEASE ?? undefined,

    // Integrations: browser tracing + session replay
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],

    // Privacy: keep user data & HTTP bodies out of events unless needed.
    dataCollection: {
      // userInfo: false,        // uncomment to stop sending user data
      // httpBodies: [],         // uncomment to stop sending HTTP request bodies
    },

    // Tracing: sample a percentage of transactions.
    // 100% during development, lower in production to control cost.
    tracesSampleRate: isProd ? 0.1 : 1.0,

    // Keep distributed tracing limited to these targets (avoid leaking to 3rd parties).
    tracePropagationTargets: [
      'localhost',
      import.meta.env.VITE_SUPABASE_URL ?? '',
    ],

    // Session Replay sample rates.
    replaysSessionSampleRate: isProd ? 0.1 : 1.0, // 10% prod, 100% dev
    replaysOnErrorSampleRate: 1.0, // 100% capture when an error occurs

    // Send `console.*` logs to Sentry.
    enableLogs: true,
  });

  initialized = true;
}

export const sentry = {
  captureException(error: unknown, context?: Record<string, unknown>): void {
    if (!initialized) {
      return;
    }
    Sentry.captureException(error, context ? { extra: context } : undefined);
  },
  captureMessage(message: string, level: Sentry.SeverityLevel = 'info'): void {
    if (!initialized) {
      return;
    }
    Sentry.captureMessage(message, level);
  },
  countMetric(name: string, value = 1): void {
    if (!initialized) {
      return;
    }
    Sentry.metrics.count(name, value);
  },
  logger: {
    info(message: string, params?: Record<string, unknown>): void {
      if (!initialized) {
        return;
      }
      Sentry.logger.info(message, params ?? {});
    },
    warn(message: string, params?: Record<string, unknown>): void {
      if (!initialized) {
        return;
      }
      Sentry.logger.warn(message, params ?? {});
    },
    error(message: string, params?: Record<string, unknown>): void {
      if (!initialized) {
        return;
      }
      Sentry.logger.error(message, params ?? {});
    },
  },
  setUser(user?: { id?: string; email?: string } | null): void {
    if (!initialized) {
      return;
    }
    if (user && user.id) {
      Sentry.setUser({ id: user.id, email: user.email });
    } else {
      Sentry.setUser(null);
    }
  },
};
